import { alpha, Grid, styled, Typography, useTheme } from "@mui/material";
import { useSelectedProject } from "Hooks/useSelectedProject";
import { LocationList } from "Components/LocationList";
import { Route, Switch, useRouteMatch } from "react-router";
import { useLocations } from "Hooks/useLocations";
import { locationService } from "Apis";
import { ApiLocation } from "@incendium/api";
import produce from "immer";
import AnimatedFab from "Components/AnimatedFab/AnimatedFab";
import { ReactNode, useLayoutEffect, useState } from "react";
import PagesWithNoLocationDrawer from "Components/Pages/PagesWithNoLocationDrawer";
import {
  ArticleOutlined,
  ChevronLeft,
  Inventory2Outlined,
} from "@mui/icons-material";
import { LocationPage } from "./LocationPage";
import { Box } from "@mui/system";
import PagesWithNoLocationList from "Components/Pages/PagesWithNoLocationList";
import ProductsWithNoLocationList from "Components/Product/ProductsWithNoLocationsList";
import { useLayoutContext } from "Providers/LayoutProvider";
import { useConfirmation } from "Hooks/useConfirmation";

const InfoCard = styled(Box)(({ theme }) => ({
  height: 320,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: theme.shape.borderRadius,
  background: "rgba(255, 255, 255, 0.9)",
  border: "1px solid rgba(201, 211, 244, 0.5)",
  boxShadow: "rgba(201, 211, 244, 0.3) 0px 0px 20px",
  transition: "box-shadow 0.2s ease, transform 0.2s ease",
  "&:hover": {
    boxShadow: "rgba(201, 211, 244, 0.6) 0px 4px 24px",
  },
}));

const InfoCardHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
}));

const InfoCardIcon = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.primary.main, 0.12),
  "& svg": {
    fontSize: 22,
  },
}));

const InfoCardBody = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  padding: theme.spacing(2),
}));

const UnassignedCard = ({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  children: ReactNode;
}) => (
  <InfoCard>
    <InfoCardHeader>
      <InfoCardIcon>{icon}</InfoCardIcon>
      <Box minWidth={0}>
        <Typography variant="subtitle1" lineHeight={1.2}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" lineHeight={1.3}>
          {subtitle}
        </Typography>
      </Box>
    </InfoCardHeader>
    <InfoCardBody>{children}</InfoCardBody>
  </InfoCard>
);

export const LocationsPage = () => {
  const theme = useTheme();
  const { selectedProject } = useSelectedProject();
  const handleDelete = useConfirmation();
  const [visible, setVisible] = useState(true);

  const { path } = useRouteMatch();
  const { locations, setLocations } = useLocations();
  const { setPageTitle } = useLayoutContext();

  useLayoutEffect(() => {
    setPageTitle(`${selectedProject?.name} Locations`);
    return () => {
      setPageTitle("");
    };
  }, [selectedProject]);

  const onDelete = (location: ApiLocation) => {
    handleDelete({
      title: `Are you sure you want to delete this location`,
      body: `This action can not be undone`,
      callback: async () => {
        await locationService.locationServiceDeleteLocation({
          projectId: selectedProject?.id as number,
          locationId: location.id as number,
        });

        setLocations(
          produce(locations, (draft) => {
            const idx = locations.findIndex((l) => l.id === location.id);
            if (idx !== -1) draft.splice(idx, 1);
          }),
        );

        return `${location.name} Deleted`;
      },
    });
  };

  return (
    <Box sx={{ height: `calc(100% - ${theme.spacing(1)})` }} pb={4}>
      <Switch>
        <Route path="/clients/:clientId/projects/:projectId/train/locations/:locationId">
          <LocationPage />

          <AnimatedFab
            onClick={() => {
              setVisible(!visible);
            }}
            variant="contained"
            color="secondary"
            visible={visible}
          >
            <ChevronLeft />
            See unassigned pages
          </AnimatedFab>
        </Route>
        <Route path={path}>
          <Grid container spacing={3}>
            {selectedProject ? (
              <>
                <Grid
                  item
                  xs={12}
                  container
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={6}
                >
                  <Grid item xs={6}>
                    <UnassignedCard
                      icon={<ArticleOutlined />}
                      title="Pages Not Assigned to Locations"
                      subtitle="Pages we've seen that aren't linked to a location yet"
                    >
                      <PagesWithNoLocationList
                        onShowMore={() => {
                          setVisible(false);
                        }}
                        limit={5}
                      />
                    </UnassignedCard>
                  </Grid>
                  <Grid item xs={6}>
                    <UnassignedCard
                      icon={<Inventory2Outlined />}
                      title="Products Not Assigned to Locations"
                      subtitle="Products we've seen that aren't linked to a location yet"
                    >
                      <ProductsWithNoLocationList limit={5} />
                    </UnassignedCard>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <LocationList onDelete={onDelete} />
                </Grid>
              </>
            ) : (
              "No Project selected"
            )}
          </Grid>
        </Route>
      </Switch>
      <PagesWithNoLocationDrawer open={!visible} setClose={setVisible} />
    </Box>
  );
};
